import type { ReactNode } from "react";

interface MarkdownContentProps {
  content: string;
}

type Block =
  | { type: "heading"; level: number; text: string }
  | { type: "paragraph"; text: string }
  | { type: "blockquote"; text: string }
  | { type: "hr" }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] };

export function MarkdownContent({ content }: MarkdownContentProps) {
  const blocks = parseBlocks(content);

  return (
    <article className="mt-6 min-w-0 space-y-5 break-words text-sm leading-7 text-content-secondary sm:mt-8">
      {blocks.map((block, index) => renderBlock(block, index))}
    </article>
  );
}

function parseBlocks(content: string) {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ type: "paragraph", text: paragraph.join(" ") });
      paragraph = [];
    }
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? "";
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      continue;
    }

    if (/^---+$/.test(trimmed)) {
      flushParagraph();
      blocks.push({ type: "hr" });
      continue;
    }

    const heading = /^(#{1,4})\s+(.+)$/.exec(trimmed);
    if (heading) {
      flushParagraph();
      blocks.push({ type: "heading", level: (heading[1] ?? "").length, text: heading[2] ?? "" });
      continue;
    }

    const unordered = /^[-*]\s+(.+)$/.exec(trimmed);
    if (unordered) {
      flushParagraph();
      const items: string[] = [unordered[1] ?? ""];
      while (index + 1 < lines.length) {
        const next = /^[-*]\s+(.+)$/.exec((lines[index + 1] ?? "").trim());
        if (!next) break;
        items.push(next[1] ?? "");
        index += 1;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    const ordered = /^\d+\.\s+(.+)$/.exec(trimmed);
    if (ordered) {
      flushParagraph();
      const items: string[] = [ordered[1] ?? ""];
      while (index + 1 < lines.length) {
        const next = /^\d+\.\s+(.+)$/.exec((lines[index + 1] ?? "").trim());
        if (!next) break;
        items.push(next[1] ?? "");
        index += 1;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    const quote = /^>\s?(.+)$/.exec(trimmed);
    if (quote) {
      flushParagraph();
      blocks.push({ type: "blockquote", text: quote[1] ?? "" });
      continue;
    }

    paragraph.push(trimmed);
  }

  flushParagraph();
  return blocks;
}

function renderBlock(block: Block, index: number) {
  if (block.type === "heading") {
    const className =
      block.level === 1
        ? "font-display text-2xl font-bold uppercase text-white sm:text-3xl"
        : "font-display text-xl font-bold uppercase text-white sm:text-2xl";
    const content = renderInline(block.text);
    if (block.level === 1) return <h2 key={index} className={className}>{content}</h2>;
    if (block.level === 2) return <h3 key={index} className={className}>{content}</h3>;
    return <h4 key={index} className="font-display text-lg font-bold uppercase text-white sm:text-xl">{content}</h4>;
  }

  if (block.type === "paragraph") return <p key={index}>{renderInline(block.text)}</p>;

  if (block.type === "blockquote") {
    return (
      <blockquote key={index} className="border-l-2 border-cyan-300/60 bg-cyan-300/5 px-3 py-3 text-cyan-50 sm:px-4">
        {renderInline(block.text)}
      </blockquote>
    );
  }

  if (block.type === "hr") return <hr key={index} className="border-cyan-300/20" />;

  if (block.type === "ul") {
    return (
      <ul key={index} className="list-disc space-y-2 pl-5 sm:pl-6">
        {block.items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item)}</li>)}
      </ul>
    );
  }

  return (
    <ol key={index} className="list-decimal space-y-2 pl-5 sm:pl-6">
      {block.items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item)}</li>)}
    </ol>
  );
}

function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));

    if (match[2] && match[3]) {
      const href = match[3];
      const isExternal = href.startsWith("http");
      nodes.push(
        <a
          key={`${match.index}-link`}
          href={href}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noreferrer" : undefined}
          className="text-cyan-100 underline decoration-cyan-300/50 underline-offset-4 transition hover:text-white"
        >
          {match[2]}
        </a>,
      );
    } else if (match[4]) {
      nodes.push(<strong key={`${match.index}-strong`} className="font-semibold text-white">{match[4]}</strong>);
    } else if (match[5]) {
      nodes.push(<em key={`${match.index}-em`} className="text-cyan-50">{match[5]}</em>);
    } else if (match[6]) {
      nodes.push(<code key={`${match.index}-code`} className="break-all rounded border border-cyan-300/20 bg-black/40 px-1.5 py-0.5 text-cyan-100">{match[6]}</code>);
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}
